import {
  infiniteQueryOptions,
  mutationOptions,
  queryOptions,
} from "@tanstack/react-query";
import { tuyau } from "@/config/tuyau";
import type { MenuSearchParams } from "@/domain/menu/menu.type";

export const menuKeys = {
  all: ["menu"] as const,
  detail: (menuId: number) => [...menuKeys.all, "detail", menuId] as const,
  chefMenus: () => [...menuKeys.all, "chef-menus"] as const,
  publicChefMenus: (chefId: number) =>
    [...menuKeys.all, "public-chef-menus", chefId] as const,
  findMenus: (searchParams: MenuSearchParams) =>
    [...menuKeys.all, "find-menus", searchParams] as const,
};

export const menuQueries = {
  detail: (menuId: number) =>
    queryOptions({
      queryKey: menuKeys.detail(menuId),
      queryFn: async () =>
        (
          await tuyau.menus.show({
            params: { id: menuId },
            query: { id: menuId },
          })
        ).data,
    }),

  chefMenus: () =>
    queryOptions({
      queryKey: menuKeys.chefMenus(),
      queryFn: async () => (await tuyau.menus.showMyMenus({})).data,
    }),

  publicChefMenus: (chefId: number) =>
    queryOptions({
      queryKey: menuKeys.publicChefMenus(chefId),
      queryFn: async () =>
        (
          await tuyau.menus.showChefMenus({
            params: { id: chefId },
            query: { id: chefId },
          })
        ).data,
    }),

  findMenus: (searchParams: MenuSearchParams) =>
    infiniteQueryOptions({
      queryKey: menuKeys.findMenus(searchParams),
      queryFn: async ({ pageParam = 1 }) => {
        const response = await tuyau.menus.search({
          query: {
            ...searchParams,
            page: pageParam,
          },
        });

        return response;
      },
      getNextPageParam: (lastPage: any) => {
        if (!lastPage.metadata.nextPageUrl) return null;
        return lastPage.metadata.currentPage + 1;
      },
      initialPageParam: 1,
    }),
};

export const menuMutations = {
  create: () =>
    mutationOptions({
      mutationFn: async (formData: FormData) =>
        await tuyau.menus.store({ body: formData as never }),
    }),

  update: () =>
    mutationOptions({
      mutationFn: async ({
        menuId,
        formData,
      }: {
        menuId: number;
        formData: FormData;
      }) =>
        await tuyau.menus.update({
          params: { id: menuId },
          body: formData as never,
        }),
    }),

  updateDraft: () =>
    mutationOptions({
      mutationFn: async ({
        menuId,
        draft,
      }: {
        menuId: number;
        draft: boolean;
      }) =>
        await tuyau.menus.updateDraft({
          params: { id: menuId },
          body: { draft },
        }),
    }),
};
